import React from 'react';

import { KebabHorizontalIcon } from '@primer/octicons-react';
import { Tooltip as PrimerTooltip } from '@primer/react';

import isFunction from 'lodash/isFunction';

import classNames from 'classnames';

import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  Filler,
  Legend,
  LineController,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  ScatterController,
  TimeScale,
} from 'chart.js';

import useChartData from '../ChartContext';

import BasicDropdown from '../../common/BasicDropdown/BasicDropdown';
import CardHeader from '../../common/Labels/CardHeader';
import CardView from '../../common/CardView/CardView';
import LoadingSpinner from '../../common/LoadingSpinner/LoadingSpinner';

import Button from '../../common/Button/Button';
import ChartCanvas from './ChartCanvas';
import ChartEmptyState from '../ChartEmptyState';
import Icon from '../../common/icons/Icon';
import { getThemeColor } from '../../../utils/theme-utils';

Chart.register(
  BarElement,
  CategoryScale,
  LineElement,
  PointElement,
  BarController,
  LineController,
  LinearScale,
  ScatterController,
  // TimeScale,
  Tooltip,
  Filler,
  Legend,
);

const ChartDropdown = ({ isDisabled }) => {
  const { dropdownOptions } = useChartData();

  if (!dropdownOptions?.length) {
    return null;
  }
  return (
    <BasicDropdown
      direction="top-left"
      dropdownLabel={null}
      iconFill="brand"
      iconSize={12}
      icon={KebabHorizontalIcon}
      isDisabled={isDisabled}
      values={dropdownOptions}
      hideCaret
      hideBorder
    />
  )
}

const IconChartMenu = () => {
  const { dropdownOptions } = useChartData();

  return (
    <div className="flex">
      {
        dropdownOptions.map(({
          id, icon, label, onClick, useTailwindSizing = false
        }) => (
          <Button
            key={id}
            id={id}
            className="m-1"
            variant="invisible"
            onClick={onClick}
          >
            <PrimerTooltip
              aria-label={label}
            >
              <Icon
                fill={getThemeColor('brand')}
                icon={icon}
                useTailwindSizing={useTailwindSizing}
                size={24}
              />
            </PrimerTooltip>
          </Button>
        ))
      }
    </div>
  )
}

const ChartCard = ({
  className,
  chartTitle,
  message,
  isLoading,
  detailRenderer,
  minHeight,
  formatXLabel = value => value,
  onPointClick = undefined,
  onClose = undefined,
  disableFullHeight = false,
  showCloseButton = false,
  hideDropdownMenu = false,
  showIconBasedMenu = false,
  loadingText = 'Loading...',
}) => {
  const { chartID, noDataAvailable } = useChartData();

  return (
    <CardView className={className} disableFullHeight={disableFullHeight}>
      <div className="flex flex-row justify-between items-center">
        <CardHeader className="mb-2">{chartTitle}</CardHeader>
        <div className="flex justify-end items-center">
          {
            showIconBasedMenu ? <IconChartMenu /> : null
          }
          {
            (hideDropdownMenu || showIconBasedMenu) ? null : <ChartDropdown isDisabled={!!message} />
          }
          {
            showCloseButton
              ? <Button
                variant="close"
                textColor="border-dark"
                hoverTextColor="border-darker"
                activeTextColor="border-darkest"
                onClick={onClose}
              /> : null
          }
        </div>
      </div>

      <div className={classNames('relative flex-grow w-full', { hidden: message || noDataAvailable })}>
        {
          isLoading && <LoadingSpinner loadingText={loadingText} animationKey={`${chartID}-spinner`} useOverlay />
        }
        <ChartCanvas
          formatXLabel={formatXLabel}
          onPointClick={onPointClick}
          minHeight={minHeight}
        />
      </div>
      {
        (noDataAvailable || message) ? (
          <div className="relative h-full flex-grow flex flex-col justify-center items-center">
            {
              isLoading ? <LoadingSpinner loadingText={loadingText} animationKey={`${chartID}-spinner`} useOverlay /> : <ChartEmptyState message={message} />
            }
          </div>
        ) : null
      }
      {
        isFunction(detailRenderer) ? detailRenderer() : null
      }
    </CardView>
  );
};
export default ChartCard;
